import { registerOverlay } from 'klinecharts'
import { NO_DEFAULT_FIGURES, type OverlayTemplateParam } from './registrationUtils'

/**
 * Custom overlay that marks an article's publish time on the chart.
 * Renders a downward-pointing triangle at the top with a dashed vertical
 * line descending through the full chart height.
 *
 * Usage: create with a single point `{ timestamp }` — the value is ignored.
 * Pass `extendData` to customize the label text.
 */
export function registerArticleMarkerOverlay(): void {
  registerOverlay({
    name: 'articleMarker',
    totalStep: 2,
    ...NO_DEFAULT_FIGURES,
    createPointFigures: ({ coordinates, bounding, overlay }) => {
      if (coordinates.length === 0) return []

      const x = coordinates[0].x
      const label = (overlay.extendData as string) ?? ''
      const figures: Array<{
        type: string
        attrs: unknown
        styles?: unknown
        ignoreEvent?: boolean
      }> = []

      // Dashed vertical line spanning full chart height
      figures.push({
        type: 'line',
        attrs: {
          coordinates: [
            { x, y: 0 },
            { x, y: bounding.height },
          ],
        },
        styles: {
          style: 'dashed',
          color: 'rgba(124, 58, 237, 0.35)',
          size: 1,
          dashedValue: [4, 3],
        },
        ignoreEvent: true,
      })

      // Small filled downward triangle at the top
      const triSize = 6
      const triY = 8
      figures.push({
        type: 'polygon',
        attrs: {
          coordinates: [
            { x: x - triSize, y: triY },
            { x: x + triSize, y: triY },
            { x, y: triY + triSize * 1.5 },
          ],
        },
        styles: {
          style: 'fill',
          color: 'rgba(124, 58, 237, 0.85)',
        },
        ignoreEvent: true,
      })

      // Text label above the triangle
      if (label) {
        figures.push({
          type: 'text',
          attrs: {
            x,
            y: triY + triSize * 1.5 + 4,
            text: label,
            align: 'center',
            baseline: 'top',
          },
          styles: {
            style: 'none',
            color: 'rgba(124, 58, 237, 0.9)',
            size: 10,
            weight: 'bold',
            family: 'inherit',
          },
          ignoreEvent: true,
        })
      }

      return figures
    },
  } as OverlayTemplateParam)
}
